/**
 * 顶栏「我 xxx」：点击打开资料弹窗，可改昵称、换头像。
 * 数据流：profile/selfId（来自 useImSession）→ 展示；保存走
 * onSaveNickname / onUploadAvatar 回调（hook 内写 OpenIM 并回读刷新）。
 */
import { useEffect, useRef, useState } from "react";
import { Avatar, Button, Flex, Input, Modal, Typography, message } from "antd";
import type { SelfProfile } from "../types";

type Props = {
  /** OpenIM userID，弹窗标题兜底 */
  selfId: string;
  /** 当前资料；未拉到时可为 null */
  profile: SelfProfile | null;
  /** 离线时禁止编辑 */
  disabled?: boolean;
  /** 保存昵称 */
  onSaveNickname: (nickname: string) => Promise<void>;
  /** 上传并更新头像 */
  onUploadAvatar: (file: File) => Promise<void>;
};

export function SelfProfileButton({
  selfId,
  profile,
  disabled,
  onSaveNickname,
  onUploadAvatar,
}: Props) {
  // 弹窗开关
  const [open, setOpen] = useState(false);
  // 昵称输入草稿
  const [nick, setNick] = useState("");
  // 保存/上传中，防连点
  const [busy, setBusy] = useState(false);
  // 隐藏的 file input，用按钮触发 click
  const fileRef = useRef<HTMLInputElement>(null);

  // 打开弹窗时用最新资料填表
  useEffect(() => {
    if (!open) return;
    setNick(profile?.nickname || "");
  }, [open, profile]);

  // 顶栏展示名：优先昵称，否则 userID
  const label = (profile?.nickname || profile?.userID || selfId || "—").trim();
  // 头像 URL（OpenIM faceURL；MinIO 需 publicRead 才能被 <img> 直接打开）
  const face = profile?.faceURL || "";
  // Avatar 上的字：取展示名首字
  const initial = label ? label.slice(0, 1) : "?";
  // 图片加载失败时提示（否则 antd 静默回退字母，像「没更新」）
  const onAvatarBroken = () => {
    if (!face) return;
    message.warning(
      "头像地址无法加载" +
        face.slice(0, 120),
    );
  };

  /** 保存昵称 */
  const saveNick = async () => {
    const t = nick.trim();
    if (!t) {
      message.warning("昵称不能为空");
      return;
    }
    setBusy(true);
    try {
      await onSaveNickname(t);
      message.success("昵称已更新");
      setOpen(false);
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /** 选中文件后上传头像 */
  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      await onUploadAvatar(file);
      message.success("头像已更新");
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      // 允许同一文件再次选择
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <>
      {/* 顶栏可点击区域：头像 + 「我 xxx」 */}
      <Flex
        align="center"
        gap={8}
        style={{
          marginLeft: "auto",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
        }}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      >
        <Avatar
          size={28}
          src={face || undefined}
          style={{ background: "#1e293b" }}
          onError={() => {
            onAvatarBroken();
            return false;
          }}
        >
          {initial}
        </Avatar>
        <Typography.Text type="secondary">我 {label}</Typography.Text>
      </Flex>

      {/* 编辑弹窗 */}
      <Modal
        title="个人资料"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Flex vertical gap={16} align="center">
          {/* 大头像预览 */}
          <Avatar
            size={72}
            src={face || undefined}
            style={{ background: "#1e293b" }}
            onError={() => {
              onAvatarBroken();
              return false;
            }}
          >
            {initial}
          </Avatar>

          {/* 隐藏文件选择；按钮触发 */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
          <Button
            disabled={disabled || busy}
            onClick={(e) => {
              // 阻止冒泡（若外层再包可点击容器时不会误关/误开）
              e.stopPropagation();
              fileRef.current?.click();
            }}
          >
            更换头像
          </Button>

          {/* 只读 OpenIM ID，避免用户改乱身份映射 */}
          <Flex vertical style={{ width: "100%" }} gap={4}>
            <Typography.Text type="secondary">OpenIM ID</Typography.Text>
            <Input value={profile?.userID || selfId} disabled />
          </Flex>

          {/* 可编辑昵称 */}
          <Flex vertical style={{ width: "100%" }} gap={4}>
            <Typography.Text type="secondary">昵称</Typography.Text>
            <Input
              value={nick}
              disabled={disabled || busy}
              maxLength={64}
              onChange={(e) => setNick(e.target.value)}
              onPressEnter={() => void saveNick()}
            />
          </Flex>

          <Button type="primary" block loading={busy} disabled={disabled} onClick={() => void saveNick()}>
            保存昵称
          </Button>
        </Flex>
      </Modal>
    </>
  );
}